import type { Card } from '@tctp/protocol';
import type { CSSProperties } from 'react';
import { cardView } from '../cardView';

export type CardSize = 'xs' | 'sm' | 'md' | 'lg';

/** Suit shapes on a 24 × 24 grid, filled with the card's text colour. */
const SUIT_PATHS: Record<string, string> = {
  hearts: 'M12 21S3 15.2 3 9.4C3 6.6 5.2 4.5 7.8 4.5c1.8 0 3.3 1 4.2 2.4.9-1.4 2.4-2.4 4.2-2.4C18.8 4.5 21 6.6 21 9.4 21 15.2 12 21 12 21z',
  diamonds: 'M12 2l7.5 10L12 22 4.5 12z',
  spades: 'M12 2C9.2 6 3.5 9.2 3.5 13.3c0 2.5 1.9 4.2 4.2 4.2 1.3 0 2.4-.5 3.2-1.3-.2 1.9-1 3.4-2.4 4.8h7c-1.4-1.4-2.2-2.9-2.4-4.8.8.8 1.9 1.3 3.2 1.3 2.3 0 4.2-1.7 4.2-4.2C20.5 9.2 14.8 6 12 2z',
  clubs: 'M12 2.6a3.7 3.7 0 0 0-3.2 5.5A3.7 3.7 0 1 0 10.9 14c-.2 2-1 3.6-2.5 5h7.2c-1.5-1.4-2.3-3-2.5-5a3.7 3.7 0 1 0 2.1-5.9A3.7 3.7 0 0 0 12 2.6z',
};

function Suit({ suit, className }: { suit: string; className: string }) {
  return <svg className={className} viewBox="0 0 24 24" aria-hidden="true"><path d={SUIT_PATHS[suit]} fill="currentColor" /></svg>;
}

/**
 * One card, drawn the way PokerNow draws them: rank at the top-left in a display serif, a large suit shape lower right,
 * plus a small suit pip under the rank so overlapped cards still show their suit.
 * `marked` = chosen as the discard. `dealDelayMs` plays the deal-in animation after that delay; `flip` also flips it face-up on landing.
 * `reveal` only flips it over where it is (a card turned face-up in a player's hand).
 */
export function PlayingCard({ card, size = 'md', faceDown = false, dim = false, marked = false, dealDelayMs, flip = false, reveal = false, used = false, unused = false, tilt = 0, onClick, disabled = false, style, className = '' }: {
  card?: Card; size?: CardSize; faceDown?: boolean; dim?: boolean; marked?: boolean; dealDelayMs?: number; flip?: boolean; reveal?: boolean;
  used?: boolean; unused?: boolean; tilt?: number;   // showdown highlight: a card of the winning hand lifts with a small tilt, the others fade
  onClick?: () => void; disabled?: boolean; style?: CSSProperties; className?: string;
}) {
  const dealt = dealDelayMs !== undefined;
  const cls = ['pcard', size, dim ? 'dim' : '', marked ? 'marked' : '', dealt ? 'dealt' : '', dealt && flip ? 'flip' : '', reveal ? 'reveal' : '', used ? 'used' : '', unused ? 'unused' : '', className].filter(Boolean).join(' ');
  style = used && tilt !== 0 ? { ...style, rotate: `${tilt}deg` } : style;
  const styles: CSSProperties = dealt ? { ...style, ['--deal-delay' as string]: `${dealDelayMs}ms` } : { ...style };
  if (faceDown || card === undefined) {
    return <span className={`${cls} back`} style={styles} aria-label="face-down card"><span className="back-mark">PP</span></span>;
  }
  const v = cardView(card);
  const body = <><span className="rank">{v.rank}</span><Suit suit={v.suit} className="pip" /><Suit suit={v.suit} className="suit" /></>;
  const colour = `${v.red ? 'red' : 'black'} ${v.suit}`;   // red/black for the tests and the back-compat, the suit for the four-colour deck
  if (onClick || disabled) {   // `disabled` keeps a card a button after it stops being clickable, so it is not remounted (and re-dealt)
    return <button type="button" className={`${cls} ${colour}`} style={styles} aria-label={v.label} aria-pressed={marked} onClick={onClick} disabled={disabled}>{body}</button>;
  }
  return <span role="img" className={`${cls} ${colour}`} style={styles} aria-label={v.label}>{body}</span>;
}
